import { motion } from "framer-motion";
import { useState, useEffect, useCallback } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { Star, Quote, ChevronLeft, ChevronRight } from "lucide-react";

const ease = [0.2, 0.8, 0.2, 1];

// Sample testimonials inspired by typical Pikko Pet Care reviews — replace with real Google reviews
const REVIEWS = [
  {
    name: "Andini P.",
    initials: "AP",
    rating: 5,
    when: "2 minggu lalu",
    body:
      "Klinik bersih, dokternya ramah dan teliti. Kucing saya yang awalnya stres jadi tenang. Tim grooming juga rapi banget hasilnya.",
    service: "Grooming · Konsultasi",
  },
  {
    name: "Reyhan A.",
    initials: "RA",
    rating: 5,
    when: "1 bulan lalu",
    body:
      "Dokter Arga handle hamster saya dengan sangat profesional. Penjelasannya detail dan harga wajar. Highly recommended buat exotic pet.",
    service: "Exotic Veterinarian",
  },
  {
    name: "Sarah M.",
    initials: "SM",
    rating: 5,
    when: "3 minggu lalu",
    body:
      "Pet hotel-nya bagus, kucing saya dirawat dengan baik selama saya keluar kota. Dapat update foto rutin. Worth banget.",
    service: "Pet Hotel",
  },
  {
    name: "Bayu R.",
    initials: "BR",
    rating: 5,
    when: "1 bulan lalu",
    body:
      "Anjing saya darurat malam hari, untungnya Pikko buka emergency. Penanganannya cepat dan kondisi langsung membaik. Terima kasih tim Pikko!",
    service: "Emergency Care",
  },
  {
    name: "Lestari W.",
    initials: "LW",
    rating: 5,
    when: "2 bulan lalu",
    body:
      "Drh. Shafira sangat sabar menjelaskan kondisi kucing saya. Vaksinasi & checkup rutin selalu di sini. Pelayanan top.",
    service: "Vaksinasi · Konsultasi",
  },
  {
    name: "Daffa S.",
    initials: "DS",
    rating: 4,
    when: "5 minggu lalu",
    body:
      "Tempatnya nyaman, ruang tunggu rapi. Booking via WhatsApp responsif. Akan kembali untuk grooming bulanan.",
    service: "Grooming",
  },
];

function Stars({ count = 5 }) {
  return (
    <div className="flex gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          className="w-3.5 h-3.5"
          style={{
            fill: i < count ? "#EF6F2C" : "transparent",
            color: i < count ? "#EF6F2C" : "rgba(138,107,82,0.4)",
          }}
        />
      ))}
    </div>
  );
}

export default function Testimonials() {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "start",
    loop: true,
    skipSnaps: false,
    dragFree: false,
  });
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);

  const update = useCallback(() => {
    if (!emblaApi) return;
    setCanPrev(emblaApi.canScrollPrev());
    setCanNext(emblaApi.canScrollNext());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    update();
    emblaApi.on("select", update);
    emblaApi.on("reInit", update);
  }, [emblaApi, update]);

  return (
    <section
      id="testimonials"
      data-testid="testimonials-section"
      className="relative py-24 md:py-32 bg-[var(--pikko-cream)] overflow-hidden"
    >
      <div
        aria-hidden
        className="absolute -top-40 right-1/4 w-[600px] h-[600px] rounded-full opacity-25 blur-3xl"
        style={{
          background:
            "radial-gradient(circle, rgba(239,111,44,0.18), transparent 60%)",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-end mb-12 lg:mb-16">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.85, ease }}
            className="lg:col-span-7"
          >
            <span className="eyebrow">— Loved by Pet Owners</span>
            <h2 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-[56px] leading-[1.05] tracking-tight text-[var(--pikko-brown-ink)]">
              Ratusan owner mempercayakan{" "}
              <span className="text-[var(--pikko-orange)]">Pikko</span>.
            </h2>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.85, delay: 0.1, ease }}
            className="lg:col-span-5 flex flex-col gap-4"
          >
            <div className="flex items-center gap-4">
              <div className="font-serif text-[52px] leading-none text-[var(--pikko-orange-dark)] font-bold">
                4.5
              </div>
              <div>
                <Stars count={5} />
                <div className="mt-1 text-[12px] text-[var(--pikko-brown-deep)]/80">
                  Berdasarkan <strong>189+</strong> review Google · diperbarui
                  berkala
                </div>
              </div>
            </div>
            <p className="text-[13.5px] leading-relaxed text-[var(--pikko-brown-deep)]/80 max-w-md">
              Cerita owner yang telah mempercayakan kesehatan dan perawatan
              hewan kesayangan mereka di Pikko Pet Care.
            </p>
          </motion.div>
        </div>

        <div className="relative">
          <div ref={emblaRef} className="overflow-hidden">
            <div className="flex gap-5 lg:gap-6">
              {REVIEWS.map((r, i) => (
                <motion.article
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.7, delay: (i % 3) * 0.08, ease }}
                  data-testid={`testimonial-card-${i}`}
                  className="flex-[0_0_85%] sm:flex-[0_0_50%] lg:flex-[0_0_33.333%] min-w-0 soft-card p-7 lg:p-8 flex flex-col"
                >
                  <div className="flex items-start justify-between mb-5">
                    <div className="flex items-center gap-3">
                      <div className="w-11 h-11 rounded-full bg-[var(--pikko-orange)]/14 flex items-center justify-center font-serif text-[14px] font-semibold text-[var(--pikko-orange-dark)]">
                        {r.initials}
                      </div>
                      <div>
                        <div className="font-serif text-[16px] text-[var(--pikko-brown-ink)] leading-tight font-semibold">
                          {r.name}
                        </div>
                        <div className="text-[11px] text-[var(--pikko-brown)]/70">
                          {r.when}
                        </div>
                      </div>
                    </div>
                    <Quote className="w-7 h-7 text-[var(--pikko-orange)]/30" />
                  </div>
                  <Stars count={r.rating} />
                  <p className="mt-4 text-[14px] leading-relaxed text-[var(--pikko-brown-deep)]/90 flex-1">
                    "{r.body}"
                  </p>
                  <div className="mt-5 pt-5 border-t border-[rgba(138,107,82,0.12)] flex items-center justify-between">
                    <span className="text-[10.5px] uppercase tracking-[0.22em] text-[var(--pikko-orange-dark)] font-semibold">
                      {r.service}
                    </span>
                    <span className="text-[10px] text-[var(--pikko-brown)]/60">
                      via Google
                    </span>
                  </div>
                </motion.article>
              ))}
            </div>
          </div>

          <div className="mt-9 flex items-center justify-between gap-4">
            <a
              href="https://maps.app.goo.gl/xbnCNzMZn5huayTV8"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[12px] uppercase tracking-[0.22em] text-[var(--pikko-orange-dark)] font-semibold hover:text-[var(--pikko-brown-ink)] transition-colors"
              data-testid="testimonials-google-link"
            >
              Lihat semua review di Google →
            </a>
            <div className="flex items-center gap-2.5">
              <button
                aria-label="Previous"
                onClick={() => emblaApi?.scrollPrev()}
                disabled={!canPrev}
                data-testid="testimonials-prev"
                className="w-11 h-11 rounded-full border border-[rgba(138,107,82,0.25)] hover:bg-[var(--pikko-cream-warm)] hover:border-[var(--pikko-orange)] text-[var(--pikko-brown-deep)] flex items-center justify-center transition-all disabled:opacity-40"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                aria-label="Next"
                onClick={() => emblaApi?.scrollNext()}
                disabled={!canNext}
                data-testid="testimonials-next"
                className="w-11 h-11 rounded-full bg-[var(--pikko-orange)] hover:bg-[var(--pikko-orange-dark)] text-white flex items-center justify-center transition-all disabled:opacity-40"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
